// ─────────────────────────────────────────────────────────────────────────────
// stylus_bindings.ts — Side-button shortcut bindings for the stylus
//
// Three triggers, one simple state machine:
//
//   draw-while-pressed   — side held at pen-down. Fires IMMEDIATELY at
//                          pen-down (action is applied and HELD for the
//                          duration of the stroke). At pen-up the canvas
//                          reverts whatever state the held action set.
//                          Default: tool-swap.
//
//   press-while-drawing  — stroke that started without side held; side
//                          pressed AFTER the stroke has clearly moved
//                          (past the displacement threshold). Fires once.
//                          Default: noop.
//
//   press-and-tap        — pen-up after a side-held gesture that never
//                          moved past the displacement threshold. Fires
//                          IN ADDITION to draw-while-pressed (which the
//                          canvas reverts before press-and-tap fires, so
//                          a press-and-tap of a tool-swap binding leaves
//                          the original tool intact and only opens the
//                          menu). Default: open-stylus-menu.
//
// Late-side-press grace: Windows occasionally exposes the side-button
// state on a separate pointerdown(button=2) event a moment AFTER the
// tip pointerdown (on Slim Pen 2 with the OS treating side+tip as a
// "right click"). onSidePressedMidStroke folds that case into "side
// held" as long as the stroke hasn't moved past threshold yet — then
// fires draw-while-pressed just like a normal pen-down with side held.
// If the stroke HAS moved, it's a real mid-draw side press and
// press-while-drawing fires instead.
// ─────────────────────────────────────────────────────────────────────────────

export type StylusActionId =
  | 'noop'
  | 'open-stylus-menu'
  | 'invoke-cast'
  | 'tool-swap'
  | 'shape-boost'
  | 'select'
  | 'draw-line'
  | 'draw-circle'
  | 'draw-airbrush';

export interface StylusActionDef {
  id: StylusActionId;
  label: string;
  short: string;
}

export const STYLUS_ACTIONS: StylusActionDef[] = [
  { id: 'noop',             label: 'None',         short: 'None' },
  { id: 'open-stylus-menu', label: 'Stylus menu',  short: 'Menu' },
  { id: 'invoke-cast',      label: 'Cast',         short: 'Cast' },
  { id: 'tool-swap',        label: 'Swap tool',    short: 'Swap' },
  { id: 'shape-boost',      label: 'Auto-shape',   short: 'Shape' },
  { id: 'select',           label: 'Select',       short: 'Sel'  },
  { id: 'draw-line',        label: 'Draw line',    short: 'Line' },
  { id: 'draw-circle',      label: 'Draw circle',  short: 'Circ' },
  { id: 'draw-airbrush',    label: 'Airbrush',     short: 'Air'  },
];

export type StylusTrigger =
  | 'draw-while-pressed'
  | 'press-while-drawing'
  | 'press-and-tap';

export interface StylusTriggerDef {
  id: StylusTrigger;
  label: string;
  hint: string;
}

export const STYLUS_TRIGGERS: StylusTriggerDef[] = [
  { id: 'draw-while-pressed',
    label: 'Draw while pressed',
    hint: 'side held at pen-down — fires immediately, action stays applied for the whole stroke.' },
  { id: 'press-while-drawing',
    label: 'Press while drawing',
    hint: 'side toggled mid-stroke, after the stroke has clearly started drawing. One-shot.' },
  { id: 'press-and-tap',
    label: 'Press and tap',
    hint: 'side held while the tip taps without moving — fires alongside the released draw-while-pressed.' },
];

export const DEFAULT_BINDINGS: Record<StylusTrigger, StylusActionId> = {
  // Most useful default: side held while drawing → boost autoshape so
  // the resulting stroke is aggressively snapped to a clean primitive.
  'draw-while-pressed':  'shape-boost',
  // Side toggled mid-draw → opt-in to the same boost from that point
  // on (the canvas keeps _shapeBoostHeld active for the rest of the
  // stroke). User can still rebind to None / Cast / etc.
  'press-while-drawing': 'shape-boost',
  // Tap with side held → stylus menu, unchanged.
  'press-and-tap':       'open-stylus-menu',
};

// Displacement threshold (page-pixels). A side-held stroke that
// stays below this is a tap (press-and-tap fires at lift); a stroke
// that crosses it is a draw (no press-and-tap). Also gates the late-
// arrival grace in onSidePressedMidStroke: side-press before the
// threshold counts as "held from the start", side-press after counts
// as a real mid-draw press.
const STROKE_MOVED_THRESHOLD_PX = 36;

/** Engine that resolves the three side-button triggers.
 *
 *  Simple model: draw-while-pressed fires the moment side becomes
 *  known to be held (pen-down or late grace) and the canvas treats
 *  the action as a HELD modal state. press-and-tap fires at pen-up
 *  when the stroke didn't move; the canvas first reverts the held
 *  state, then runs press-and-tap's action — so a press-and-tap of
 *  Menu while draw-while-pressed is Swap leaves the tool unchanged
 *  and just opens the menu. */
export class StylusBindingEngine {
  private _bindings: Record<StylusTrigger, StylusActionId> = { ...DEFAULT_BINDINGS };
  // True while the engine considers the side button held for the
  // current stroke (set at pen-down or via late-arrival grace).
  private _sideHeld = false;
  // True once the stroke has moved past STROKE_MOVED_THRESHOLD_PX —
  // tracked independently of side state so onSidePressedMidStroke can
  // tell late-arrival apart from a genuine mid-draw side press.
  private _strokeHasMoved = false;
  // Last trigger that fired this stroke (or null). Useful for
  // canvas-side bookkeeping at stroke end.
  private _firedTrigger: StylusTrigger | null = null;
  // Original pen-down position, never re-anchored — used purely for
  // the strokeHasMoved test.
  private _strokeStartX = 0;
  private _strokeStartY = 0;

  /** Fires when a trigger resolves. The fourth argument is the
   *  trigger that resolved, so the canvas can apply trigger-aware
   *  behaviour (e.g. tool-swap on draw-while-pressed is a HELD modal
   *  state reverted at pen-up; tool-swap on press-and-tap is a
   *  one-shot toggle). */
  onAction: ((action: StylusActionId, x: number, y: number, trigger: StylusTrigger) => void) | null = null;

  /** Fires once at pen-up for any side-held stroke (whether or not a
   *  trigger fired). Used by the canvas to revert the held state set
   *  by draw-while-pressed (tool swap, etc.). */
  onStrokeEnd: ((firedTrigger: StylusTrigger | null) => void) | null = null;

  // ── Bindings registry ──────────────────────────────────────────────────

  getBinding(trigger: StylusTrigger): StylusActionId {
    return this._bindings[trigger] ?? 'noop';
  }

  setBinding(trigger: StylusTrigger, action: StylusActionId): void {
    this._bindings[trigger] = action;
  }

  serialize(): Record<string, StylusActionId> {
    return { ...this._bindings };
  }

  deserialize(obj: Record<string, StylusActionId> | undefined): void {
    if (!obj) return;
    for (const t of ['draw-while-pressed', 'press-while-drawing', 'press-and-tap'] as StylusTrigger[]) {
      const v = obj[t];
      if (typeof v === 'string') this._bindings[t] = v as StylusActionId;
    }
  }

  // ── Trigger detection ──────────────────────────────────────────────────

  /** True while the current stroke is side-held — the canvas reads
   *  this for HUD diagnostics; trigger-aware behaviour goes through
   *  onAction's `trigger` argument instead. */
  get sideHeldDuringStroke(): boolean { return this._sideHeld; }

  /** Pen-down. If side is held at this instant, fire draw-while-
   *  pressed immediately so the canvas can apply (and remember to
   *  revert) the held state for the whole stroke. */
  onPenDown(buttons: number, x: number, y: number): void {
    this._sideHeld = (buttons & 2) !== 0;
    this._strokeHasMoved = false;
    this._firedTrigger = null;
    this._strokeStartX = x;
    this._strokeStartY = y;
    if (this._sideHeld) {
      this._fire('draw-while-pressed', x, y);
    }
  }

  /** pointermove sample. Drives _strokeHasMoved (which is
   *  side-state-independent so onSidePressedMidStroke can decide
   *  late-arrival vs. real mid-draw press). Fed unconditionally
   *  by the canvas. */
  notifyMove(x: number, y: number): void {
    if (this._strokeHasMoved) return;
    const dx = x - this._strokeStartX;
    const dy = y - this._strokeStartY;
    if (Math.sqrt(dx * dx + dy * dy) > STROKE_MOVED_THRESHOLD_PX) {
      this._strokeHasMoved = true;
    }
  }

  /** pointerdown(button=2) during an active stroke. Two cases:
   *
   *  a) stroke hasn't moved yet → Windows is delivering side-button
   *     state late. Promote to "side held" and fire draw-while-
   *     pressed (just like a normal pen-down with side held).
   *
   *  b) stroke has clearly moved → genuine mid-draw side press.
   *     Fire press-while-drawing as a one-shot. */
  onSidePressedMidStroke(x: number, y: number): void {
    if (this._sideHeld) return;
    if (this._strokeHasMoved) {
      this._fire('press-while-drawing', x, y);
      return;
    }
    this._sideHeld = true;
    this._fire('draw-while-pressed', x, y);
  }

  /** Pen-up. If the side-held stroke never moved, press-and-tap
   *  fires (in addition to whatever draw-while-pressed already
   *  did). Then onStrokeEnd lets the canvas revert any held
   *  modal state.
   *
   *  onStrokeEnd fires whenever ANY trigger fired this stroke —
   *  including press-while-drawing (which doesn't latch _sideHeld)
   *  — so canvas-side held flags can't leak past the gesture even
   *  if the side button was released mid-stroke. */
  onPenUp(x: number, y: number): void {
    if (this._sideHeld && !this._strokeHasMoved) {
      this._fire('press-and-tap', x, y);
    }
    const fired = this._firedTrigger;
    this._sideHeld = false;
    this._strokeHasMoved = false;
    this._firedTrigger = null;
    if (fired !== null) {
      this.onStrokeEnd?.(fired);
    }
  }

  /** Force-clear (view switch / page change / cancelled stroke).
   *  Does not fire onStrokeEnd — the canvas uses its own cancel
   *  paths for any held-state reverts. */
  reset(): void {
    this._sideHeld = false;
    this._strokeHasMoved = false;
    this._firedTrigger = null;
  }

  private _fire(trigger: StylusTrigger, x: number, y: number): void {
    const action = this._bindings[trigger];
    this._firedTrigger = trigger;
    if (action && action !== 'noop') {
      this.onAction?.(action, x, y, trigger);
    }
  }
}
